import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { userErrorMessage } from "@/lib/user-error";

// The "Anzahl" of company-profiles/0006-company-page-mvp §2, plus the way to the rest of the
// roster now that genesis pages it (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not).
//
// EXPLICIT, NOT INFINITE SCROLL. §2 asks for a compact strip on a showcase page — a count and a way
// in, not a feed that grows as you fall down it. So the next page is a thing the reader asks for,
// once per click, and a reader who never asks pays for one page.
//
// SHARED BY THE STRIP AND THE MEMBER EDITOR, which is the whole reason it is its own component. An
// admin who could only ever reach the first page could not remove member thirteen, so the control
// has to exist in edit mode too — and there it must be the same control, reading the same numbers.
//
// Neutral throughout: the count and the "not all of them" state are sentences on the meta rung, and
// the only colour is the button's own role. Nothing here is a warning.

export interface CompanyMemberCountProps {
  /** How many rows are on screen right now — the pages loaded so far, not the roster. */
  shown: number;
  /**
   * genesis's `totalCount`: the members it lists.
   *
   * THE SAME NUMBER FOR EVERY CALLER since concept v5 — a member is on the roster because a membership
   * row says so, and no longer because they published CURRENT_EMPLOYER, so a stranger and a colleague
   * now count the same people. (It was per-caller until 2026-08-24; @external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §7 is the record.)
   *
   * STILL NOT THE COMPANY'S HEADCOUNT, and never worded as one: it counts active people who hold a
   * membership row here, which is a different thing from how many staff the company has. The company's
   * own stated size lives in the page header as `employeeCount`.
   */
  total: number;
  hasMore: boolean;
  loading: boolean;
  /** A failed load, said neutrally. Never rendered raw — @adr SPARK-0010-no-raw-server-errors-in-ui@1. */
  error: Error | null;
  onLoadMore: () => void;
}

export function CompanyMemberCount({
  shown,
  total,
  hasMore,
  loading,
  error,
  onLoadMore,
}: CompanyMemberCountProps) {
  const intl = useIntl();
  if (total === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <p className="field-meta text-ink/55">
        {shown < total ? (
          <FormattedMessage
            id="company.page.members.countPartial"
            defaultMessage="{shown} of {total} people shown"
            values={{ shown, total }}
          />
        ) : (
          <FormattedMessage
            id="company.page.members.count"
            defaultMessage="{count, plural, one {# person} other {# people}}"
            values={{ count: total }}
          />
        )}
      </p>
      {hasMore ? (
        <Button variant="secondary" size="compact" disabled={loading} onClick={onLoadMore}>
          {loading ? (
            <FormattedMessage
              id="company.page.members.loadingMore"
              defaultMessage="Loading people"
            />
          ) : (
            <FormattedMessage
              id="company.page.members.showMore"
              defaultMessage="Show more people"
            />
          )}
        </Button>
      ) : null}
      {/* The page itself loaded; one more page did not. Said here rather than turned into the
          page's failure state, and through userErrorMessage so genesis's own words never reach the
          reader. The button stays enabled, so the retry is the same click as the first attempt. */}
      {error ? <p className="field-meta text-ink/80">{userErrorMessage(intl, error)}</p> : null}
    </div>
  );
}
